import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, type ApiError, type GlobalSearchResult } from '@whinerp/client';
import { EmptyState, ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { objectLabelKey } from '../records/record-page.js';
import { SearchResults } from './search-results.js';

/**
 * SC-05 Global search results (F-020).
 *
 * The overlay shows the first few per object; this is where somebody goes when
 * the answer was not in them. Same component renders both lists, so a result
 * cannot read one way in the dropdown and another way here.
 */
function SearchScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const [params] = useSearchParams();
  const term = (params.get('q') ?? '').trim();

  const results = useQuery<GlobalSearchResult, ApiError>({
    queryKey: ['search-page', term],
    queryFn: () => api.globalSearch({ query: { q: term, limit: 20 } }),
    enabled: term.length > 0,
  });

  return (
    <div data-component="SearchScreen" className="flex flex-col gap-5">
      <PageHeading>{t('search.title')}</PageHeading>

      {term === '' ? (
        /* Waiting for a query, not reporting that a search found nothing.
           Two different sentences and two different things to do. */
        <EmptyState
          reason="awaiting-input"
          illustration="MagnifyingGlass"
          title={t('search.hint')}
          body=""
          primary={{ label: t('empty.action.search'), icon: 'MagnifyingGlass', href: '/search' }}
        />
      ) : results.isPending ? (
        <Skeleton className="h-block-lg w-full" />
      ) : results.isError ? (
        <ErrorState
          title={t('state.error')}
          body={results.error.message}
          onRetry={() => void results.refetch()}
        />
      ) : results.data.groups.length === 0 ? (
        <EmptyState
          reason="filtered-to-nothing"
          illustration="MagnifyingGlass"
          title={t('search.none', { values: { term } })}
          /*
           * Where it looked, beside "nothing matched".
           *
           * Without it the sentence is a claim about the workspace when it is
           * a claim about the query - and it was false for four object types
           * the box could not see at all. Naming the places makes an empty
           * result readable, and makes a missing one visible the moment
           * somebody notices a kind of record is absent from the list.
           */
          body={t('search.lookedIn', {
            values: {
              count: formatCount(results.data.searched.length, format),
              // The plural label each object is called on its own screen, so
              // the list reads in the words the product uses everywhere else.
              objects: results.data.searched
                .map((key) => t(objectLabelKey(`${key}.plural`)))
                .join(', '),
            },
          })}
          primary={{ label: t('empty.action.clearSearch'), icon: 'X', href: '/search' }}
        />
      ) : (
        <Panel>
          <SearchResults groups={results.data.groups} />
        </Panel>
      )}
    </div>
  );
}

function SearchLoading(): JSX.Element {
  return <Skeleton className="h-block-lg w-full" />;
}

export const screen = defineScreen({
  id: 'SC-05',
  route: '/search',
  surface: 'page',
  title: 'Search results',
  features: ['F-020'],
  entitlement: 'F-020',
  // The search box in the top bar is on every screen, and "see all results"
  // is the affordance that leads here.
  reachedBy: [{ kind: 'command', action: 'See all results' }],
  components: [],
  elements: ['SearchScreen'],
  states: {
    empty: {
      notApplicable:
        'An empty search is a prompt, not an empty collection: the screen asks for a term rather than reporting none.',
    },
    loading: SearchLoading,
    error: {
      notApplicable:
        'A failed search is a message in place of the results, with the query still in the address bar.',
    },
  },
  component: SearchScreen,
});

export default screen;
